import { useMemo } from 'react';
import { categories, trendingNews, featureStories } from '@/mocks/gossip';

interface HeroProps {
  query: string;
  onQueryChange: (q: string) => void;
  onSearch: () => void;
}

export default function Hero({ query, onQueryChange, onSearch }: HeroProps) {
  const allTitles = useMemo(
    () =>
      [...featureStories, ...trendingNews].map((n) => ({
        title: n.title,
        reads: n.reads,
        tag: n.tag,
      })),
    []
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch();
  };

  return (
    <section id="top" className="relative overflow-hidden">
      <div className="absolute inset-0 -z-10 bg-background-100">
        <div className="absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-primary-100/60 blur-3xl" />
        <div className="absolute -left-32 bottom-0 h-[380px] w-[380px] rounded-full bg-accent-100/70 blur-3xl" />
      </div>

      <div className="mx-auto grid w-full max-w-[1280px] grid-cols-1 items-center gap-10 px-4 pb-16 pt-28 md:px-6 lg:grid-cols-[1.1fr_0.9fr] lg:pt-36">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full bg-primary-500 px-3 py-1 text-xs font-medium text-white">
            <i className="ri-fire-line" />
            圈内人都在看的爆料入口
          </span>
          <h1 className="mt-6 font-heading text-4xl font-black leading-tight tracking-tight text-foreground-950 md:text-5xl">
            台前幕后，
            <br />
            不该只有一种说法。
          </h1>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-foreground-600 md:text-lg">
            99爆料汇集内部人士、知情人士与工作人员透露的小道消息和行业观察。我们认真记录、诚实标注，带你看清镜头之外与报表之下的真实一面。
          </p>

          <form
            onSubmit={handleSubmit}
            className="mt-8 flex w-full max-w-xl flex-col gap-2 sm:flex-row sm:items-center sm:gap-0 sm:rounded-lg"
          >
            <input
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder="搜关键词，例如：综艺、公关、供应链…"
              aria-label="搜索圈内消息"
              className="w-full rounded-lg border border-background-200 bg-background-50 px-4 py-3 text-sm text-foreground-900 shadow-sm outline-none transition-colors focus:border-primary-400 sm:rounded-r-none"
            />
            <button
              type="submit"
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-primary-600 sm:rounded-l-none"
            >
              <i className="ri-search-line text-base" />
              搜索爆料
            </button>
          </form>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <span className="text-xs text-foreground-500">大家都在搜：</span>
            {['综艺幕后', '公关翻车', '网红店真相', '大厂职场'].map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => {
                  onQueryChange(t);
                  onSearch();
                }}
                className="whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-3 py-1 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                {t}
              </button>
            ))}
          </div>
        </div>

        <div className="relative">
          <div className="relative overflow-hidden rounded-lg">
            <img
              src="https://readdy.ai/api/search-image?query=cinematic%20collage%20of%20backstage%20life%20behind%20the%20curtain%20with%20spotlights%20and%20shadows%20of%20crew%20members%2C%20warm%20rich%20amber%20and%20deep%20red%20tones%2C%20editorial%20photography%2C%20moody%20atmospheric%20lighting%2C%20high%20detail%2C%20abstract%20artistic%20composition&width=1040&height=820&seq=hero-collage-main&orientation=portrait"
              alt="99爆料圈内幕后见闻首图"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
          </div>
          <div className="absolute -bottom-6 -left-6 hidden max-w-[260px] rounded-lg border border-background-200 bg-background-50/95 p-4 shadow-lg backdrop-blur sm:block">
            <p className="font-heading text-2xl font-black text-primary-500">99<span className="text-foreground-900">%</span></p>
            <p className="mt-1 text-xs leading-relaxed text-foreground-600">
              编辑部坚持对每条重要线索进行多源交叉核验
            </p>
          </div>
        </div>
      </div>

      <div className="border-y border-background-200 bg-background-50">
        <div className="mx-auto flex w-full max-w-[1280px] flex-wrap items-center justify-between gap-x-6 gap-y-2 px-4 py-4 md:px-6">
          <span className="text-sm font-medium text-foreground-900">6 大圈层 · 每日滚动更新</span>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
            {categories.slice(0, 6).map((c) => (
              <a
                key={c.key}
                href="#paoliao"
                className="flex items-center gap-1.5 whitespace-nowrap text-sm text-foreground-600 transition-colors hover:text-primary-600"
              >
                <i className="ri-arrow-right-s-line text-primary-400" />
                {c.label}
              </a>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto flex w-full max-w-[1280px] flex-wrap items-center gap-3 px-4 py-3 text-xs text-foreground-500 md:px-6">
        <span className="whitespace-nowrap">今日热榜 ·</span>
        {allTitles.slice(0, 3).map((t) => (
          <a
            key={t.title}
            href="#rank"
            className="flex max-w-full items-center gap-1 whitespace-nowrap text-foreground-600 transition-colors hover:text-primary-600"
          >
            <span className="text-primary-400">#{allTitles.indexOf(t) + 1}</span>
            <span className="truncate">{t.title}</span>
          </a>
        ))}
      </div>
    </section>
  );
}